const DEFAULT_API = "https://ai-middleware.myskool.app";

export default class LiveVoiceCaption {
  constructor({
    apiBase = DEFAULT_API,
    language = "en",
    delay = "low",
    onTextChange,
    onFinalText,
  } = {}) {
    this.apiBase = String(apiBase || DEFAULT_API).replace(/\/$/, "");
    this.language = language;
    this.delay = delay;
    this.onTextChange = onTextChange;
    this.onFinalText = onFinalText;
    this.pc = null;
    this.dc = null;
    this.mediaStream = null;
    this.liveText = "";
    this.partialByItem = {};
  }

  async start() {
    this.liveText = "";
    this.partialByItem = {};

    this.pc = new RTCPeerConnection();
    this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });

    const audioTrack = this.mediaStream.getAudioTracks()[0];
    if (!audioTrack) {
      throw new Error("No microphone audio track available");
    }
    this.pc.addTrack(audioTrack, this.mediaStream);

    this.dc = this.pc.createDataChannel("oai-events");
    this.dc.onmessage = (event) => {
      const msg = JSON.parse(event.data);

      if (msg.type === "conversation.item.input_audio_transcription.delta") {
        const itemId = msg.item_id || "default";
        const prev = this.partialByItem[itemId] || "";
        this.partialByItem[itemId] = prev + (msg.delta || "");
        this.liveText = Object.values(this.partialByItem).join(" ").trim();
        this.onTextChange?.(this.liveText);
      }

      if (msg.type === "conversation.item.input_audio_transcription.completed") {
        const finalText = (msg.transcript || "").trim();
        if (finalText) {
          this.liveText = finalText;
          this.onTextChange?.(this.liveText);
          this.onFinalText?.(finalText);
        }
      }
    };

    const offer = await this.pc.createOffer({ offerToReceiveAudio: true });
    await this.pc.setLocalDescription(offer);
    await this.waitForIceGathering();

    const offerSdp = (this.pc.localDescription && this.pc.localDescription.sdp) || offer.sdp;

    const sessionUrl = `${this.apiBase}/chat/realtime-session?language=${encodeURIComponent(
      this.language
    )}&delay=${encodeURIComponent(this.delay)}`;

    const answerSdp = await fetch(sessionUrl, {
      method: "POST",
      headers: { "Content-Type": "application/sdp" },
      body: offerSdp,
    }).then(async (res) => {
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to start live transcription");
      }
      return res.text();
    });

    await this.pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
  }

  waitForIceGathering(timeoutMs = 2000) {
    return new Promise((resolve) => {
      if (!this.pc || this.pc.iceGatheringState === "complete") {
        resolve();
        return;
      }

      const timer = setTimeout(() => {
        cleanup();
        resolve();
      }, timeoutMs);

      const check = () => {
        if (this.pc && this.pc.iceGatheringState === "complete") {
          cleanup();
          resolve();
        }
      };

      const cleanup = () => {
        clearTimeout(timer);
        if (this.pc) {
          this.pc.removeEventListener("icegatheringstatechange", check);
        }
      };

      this.pc.addEventListener("icegatheringstatechange", check);
    });
  }

  stop() {
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.dc?.close();
    this.pc?.close();
    this.pc = null;
    this.dc = null;
    this.mediaStream = null;
    return this.liveText;
  }
}
